import { t } from '../../../shared/config/translations'
import type { ClientRelease } from '../../../shared/types/collection'
import { saveReleaseFavorite } from '../../api/collection'
import { getSessionSignal } from '../../api/requestSession'
import { updateCachedReleaseByInstanceId } from '../../stores/collectionStore'
import { haptic } from '../../utils/haptics'
import { showSnackbar } from '../Snackbar'
import { emitReleaseUpdated } from './releaseEvents'

class ReleaseFavoriteController {
	private readonly sessionSignal = getSessionSignal()
	private readonly buttonEl: HTMLButtonElement
	private confirmedFavorite: boolean
	private release: ClientRelease

	constructor(buttonEl: HTMLButtonElement, release: ClientRelease) {
		this.buttonEl = buttonEl
		this.release = release
		this.confirmedFavorite = !!release.isFavorite
		this.handleClick = this.handleClick.bind(this)
		this.buttonEl.addEventListener('click', this.handleClick)
		this.render(this.confirmedFavorite)
	}

	private async handleClick(): Promise<void> {
		if (this.buttonEl.disabled || this.sessionSignal.aborted) return
		const nextFavorite = !this.confirmedFavorite
		this.buttonEl.disabled = true
		void haptic('toggle')
		this.applyLocally(nextFavorite)

		try {
			const response = await saveReleaseFavorite(
				this.release.id,
				nextFavorite,
			)
			if (this.sessionSignal.aborted) return
			this.confirmedFavorite = response.isFavorite
			this.applyLocally(response.isFavorite)
			emitReleaseUpdated(this.release, 'favorite')
		} catch (error) {
			if (this.sessionSignal.aborted) return
			console.error('Failed to save release favorite', error)
			this.applyLocally(this.confirmedFavorite)
			showSnackbar(t('release.favoriteSaveFailed'), 'error')
		} finally {
			this.buttonEl.disabled = false
		}
	}

	private applyLocally(isFavorite: boolean): void {
		const changed = !!this.release.isFavorite !== isFavorite
		this.release.isFavorite = isFavorite
		this.render(isFavorite)
		updateCachedReleaseByInstanceId(this.release)
		if (changed) {
			emitReleaseUpdated(this.release, 'favorite', false)
		}
	}

	private render(isFavorite: boolean): void {
		const label = t(
			isFavorite ? 'release.removeFavorite' : 'release.addFavorite',
		)
		this.buttonEl.classList.toggle('is-favorite', isFavorite)
		this.buttonEl.setAttribute('aria-label', label)
		this.buttonEl.setAttribute('aria-pressed', String(isFavorite))
		this.buttonEl.title = label
	}
}

export function initializeReleaseFavorite(
	releaseEl: HTMLElement,
	release: ClientRelease,
): void {
	const buttonEl =
		releaseEl.querySelector<HTMLButtonElement>('#favorite-button')
	if (!buttonEl) return
	new ReleaseFavoriteController(buttonEl, release)
}
